import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Edit, Printer, Trash2, Ban, RotateCcw, X, AlertTriangle, FileText, Receipt, ShieldCheck as ClaimIcon, Link as LinkIcon, Send, Loader2, Package, Bell, MessageCircle, PenLine, CheckCircle2, CalendarPlus } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "../../lib/api";
import { statusColor } from "../../lib/jobConstants";
import { money } from "../../lib/jobCalc";
import { JobDocuments } from "../../components/admin/JobDocuments";
import { FieldSubmissionView } from "../../components/admin/FieldSubmissionView";
import { VehicleHistory } from "../../components/admin/VehicleHistory";
import { PreFitmentShareCard } from "../../components/admin/PreFitmentShareCard";
import { ClientLinkModal } from "../../components/admin/ClientLinkModal";
import { BundleSendModal } from "../../components/admin/BundleSendModal";
import { NotifyStatusModal } from "../../components/admin/NotifyStatusModal";
import { WhatsAppQuickSendModal } from "../../components/admin/WhatsAppQuickSendModal";
import { SignaturePadModal } from "../../components/admin/SignaturePadModal";
import { JobScheduleModal } from "../../components/admin/JobScheduleModal";

const DOCS = [
  { key: "field_submission", label: "Field Submission" },
  { key: "job_card", label: "Job Card" },
  { key: "claim_form", label: "Claim Form", insuranceOnly: true },
  { key: "insurance_quotation", label: "Insurance Quotation", insuranceOnly: true },
  { key: "excess_invoice", label: "Excess Invoice", insuranceOnly: true },
  { key: "order_form", label: "Order Form" },
  { key: "tax_invoice", label: "Tax Invoice" },
];

export const AdminJobDetail = () => {
  const { id } = useParams();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState("job_card");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [voidReason, setVoidReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [showClientLink, setShowClientLink] = useState(false);
  const [showBundle, setShowBundle] = useState(false);
  const [showNotify, setShowNotify] = useState(false);
  const [showWhatsApp, setShowWhatsApp] = useState(false);
  const [showSignature, setShowSignature] = useState(false);
  const [showSchedule, setShowSchedule] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await adminApi.getJob(id);
      setJob(data);
    } catch (e) {
      toast.error("Could not load job");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const printDoc = () => {
    document.body.classList.add("printing-doc");
    setTimeout(() => {
      window.print();
      setTimeout(() => document.body.classList.remove("printing-doc"), 500);
    }, 50);
  };

  const doVoid = async () => {
    setBusy(true);
    try {
      await adminApi.voidJob(id, voidReason.trim() || null);
      toast.success("Invoice voided");
      setShowDeleteModal(false);
      setVoidReason("");
      load();
    } catch { toast.error("Could not void"); }
    finally { setBusy(false); }
  };

  const doUnvoid = async () => {
    try {
      await adminApi.unvoidJob(id);
      toast.success("Void reversed");
      load();
    } catch { toast.error("Could not un-void"); }
  };

  const doIssueTaxInvoice = async () => {
    setBusy(true);
    try {
      const res = await adminApi.issueTaxInvoice(id, "Admin");
      if (res.already_issued) toast.message(`Tax Invoice ${res.tax_invoice_number} was already issued`);
      else toast.success(`Tax Invoice ${res.tax_invoice_number} issued`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not issue tax invoice");
    } finally { setBusy(false); }
  };

  const doDelete = async () => {
    if (!window.confirm(`Permanently delete Job #${job.job_number} and all six documents? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await adminApi.deleteJob(id);
      toast.success("Job deleted");
      window.location.href = "/admin/jobs";
    } catch { toast.error("Could not delete"); setBusy(false); }
  };

  if (loading) return <div className="p-10 text-center text-[#3f4b52]">Loading…</div>;
  if (!job) return null;

  const isCash = job.payment_type === "cash";
  const visibleDocs = DOCS.filter((d) => {
    if (d.insuranceOnly && isCash) return false;
    if (d.cashOnly && !isCash) return false;
    return true;
  });

  return (
    <div data-testid="admin-job-detail" data-brand={job.brand || "dd"}>
      <Link to="/admin/jobs" className="inline-flex items-center gap-2 text-xs font-display uppercase tracking-widest text-[#3f4b52] hover:text-[color:var(--brand-accent)] no-print">
        <ArrowLeft size={14} /> Back to jobs
      </Link>

      {/* Brand-context banner — reminds admin which letterhead every PDF on this page prints with. */}
      <div
        data-testid="job-brand-banner"
        className="mt-3 no-print flex items-center gap-2 rounded-md border border-[color:var(--brand-accent)]/40 bg-[color:var(--brand-tint-bg)] px-3 py-1.5"
      >
        <span className="inline-block h-2 w-2 rounded-full bg-[color:var(--brand-accent)]" />
        <span className="font-display uppercase tracking-widest text-[11px] text-[color:var(--brand-accent-strong)]">
          {job.brand === "bsg" ? "BSG Auto Glass paperwork" : "Diesel & Dust paperwork"}
        </span>
      </div>

      <div className="mt-3 flex items-start justify-between flex-wrap gap-4 no-print">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="font-display font-black uppercase text-2xl sm:text-3xl md:text-4xl text-[#111619]">
              Job #{job.job_number}
            </h1>
            <span className={`px-2 py-0.5 rounded-sm text-xs font-display uppercase tracking-wider ${statusColor(job.status)}`}>
              {job.status}
            </span>
          </div>
          <p className="text-[#3f4b52] mt-1 text-sm sm:text-base break-words">
            {job.client_name} · {job.vehicle_make_model || "—"} · {job.registration || "—"}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link
            to={`/admin/jobs/${job.id}/edit`}
            className="bg-[#20282c] text-white font-display uppercase tracking-wider text-xs px-4 h-10 rounded-md inline-flex items-center gap-2 hover:brightness-110"
            data-testid="job-edit-btn"
          >
            <Edit size={14} /> Edit
          </Link>
          <button
            onClick={() => adminApi.openDoc(adminApi.jobQuotePdfUrl(job.id))}
            type="button"
            data-testid="job-quote-pdf-btn"
            className="bg-[color:var(--brand-accent)] text-white font-display uppercase tracking-wider text-xs px-4 h-10 rounded-md inline-flex items-center gap-2 hover:brightness-110"
          >
            <FileText size={14} /> Quote PDF
          </button>
          <button
            onClick={() => adminApi.openDoc(adminApi.jobInvoicePdfUrl(job.id))}
            type="button"
            data-testid="job-invoice-pdf-btn"
            className="bg-[#20282c] text-white font-display uppercase tracking-wider text-xs px-4 h-10 rounded-md inline-flex items-center gap-2 hover:brightness-110"
          >
            <Receipt size={14} /> Invoice PDF
          </button>
          {job.insurance_company && (
            <button
              onClick={() => adminApi.openDoc(adminApi.jobClaimFormPdfUrl(job.id))}
              type="button"
              data-testid="job-claim-form-pdf-btn"
              className="bg-[#20282c] text-white font-display uppercase tracking-wider text-xs px-4 h-10 rounded-md inline-flex items-center gap-2 hover:brightness-110"
            >
              <ClaimIcon size={14} /> Claim Form PDF
            </button>
          )}
          {job.insurance_company && (Number(job.excess_pct) > 0 || Number(job.excess_amount_override) > 0) && (
            <button
              onClick={() => adminApi.openDoc(adminApi.jobExcessInvoicePdfUrl(job.id))}
              type="button"
              data-testid="job-excess-invoice-pdf-btn"
              className="bg-[color:var(--brand-accent)] text-white font-display uppercase tracking-wider text-xs px-4 h-10 rounded-md inline-flex items-center gap-2 hover:brightness-110"
            >
              <Receipt size={14} /> Excess Invoice PDF
            </button>
          )}
          {job.insurance_company && (Number(job.excess_pct) > 0 || Number(job.excess_amount_override) > 0) && (
            job.excess_paid ? (
              <button
                onClick={async () => {
                  if (!window.confirm("Undo excess-paid tick?")) return;
                  try {
                    await adminApi.clearExcessPaid(job.id);
                    toast.success("Excess mark cleared");
                    load();
                  } catch { toast.error("Could not clear"); }
                }}
                type="button"
                data-testid="job-excess-paid-clear-btn"
                className="bg-[#10b981] text-white font-display uppercase tracking-wider text-xs px-4 h-10 rounded-md inline-flex items-center gap-2 hover:brightness-110"
              >
                <CheckCircle2 size={14} /> Excess paid · {(job.excess_paid_at || "").slice(0, 10)}
              </button>
            ) : (
              <button
                onClick={async () => {
                  const method = window.prompt(
                    "Payment method (cash / eft / card)?", "cash",
                  );
                  if (!method) return;
                  try {
                    await adminApi.markExcessPaid(job.id, { method });
                    toast.success("Excess marked as paid");
                    load();
                  } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
                }}
                type="button"
                data-testid="job-mark-excess-paid-btn"
                className="border border-[#10b981] text-[#10b981] font-display uppercase tracking-wider text-xs px-4 h-10 rounded-md inline-flex items-center gap-2 hover:bg-[#f0fdf4]"
              >
                <CheckCircle2 size={14} /> Mark excess paid
              </button>
            )
          )}
          <button
            onClick={() => setShowSchedule(true)}
            type="button"
            data-testid="job-schedule-btn"
            className={`font-display uppercase tracking-wider text-xs px-4 h-10 rounded-md inline-flex items-center gap-2 hover:brightness-110 ${
              job.fitment_date
                ? "bg-[#10b981] text-white"
                : "border border-[color:var(--brand-accent)] text-[color:var(--brand-accent)] hover:bg-[color:var(--brand-accent-soft)]"
            }`}
          >
            <CalendarPlus size={14} /> {job.fitment_date ? `Fitment · ${(job.fitment_date || "").slice(0, 10)}` : "Schedule fitment"}
          </button>
          <button
            onClick={() => setShowSignature(true)}
            type="button"
            data-testid="job-signature-btn"
            className={`font-display uppercase tracking-wider text-xs px-4 h-10 rounded-md inline-flex items-center gap-2 hover:brightness-110 ${
              job.client_signature
                ? "bg-[#10b981] text-white"
                : "border border-[color:var(--brand-accent)] text-[color:var(--brand-accent)] hover:bg-[color:var(--brand-accent-soft)]"
            }`}
          >
            <PenLine size={14} /> {job.client_signature ? "Signed ✓ · re-sign" : "Capture signature"}
          </button>
          <button
            onClick={() => setShowClientLink(true)}
            data-testid="job-client-link-btn"
            className="bg-[#10b981] text-white font-display uppercase tracking-wider text-xs px-4 h-10 rounded-md inline-flex items-center gap-2 hover:brightness-110"
          >
            <LinkIcon size={14} /> Client link
          </button>
          <button
            onClick={() => setShowBundle(true)}
            data-testid="job-bundle-send-btn"
            className="bg-[#0ea5e9] text-white font-display uppercase tracking-wider text-xs px-4 h-10 rounded-md inline-flex items-center gap-2 hover:brightness-110"
          >
            <Package size={14} /> Bundle &amp; Send
          </button>
          <button
            onClick={() => setShowNotify(true)}
            data-testid="job-notify-status-btn"
            className="bg-[#f59e0b] text-white font-display uppercase tracking-wider text-xs px-4 h-10 rounded-md inline-flex items-center gap-2 hover:brightness-110"
          >
            <Bell size={14} /> Notify client
          </button>
          <button
            onClick={() => setShowWhatsApp(true)}
            data-testid="job-whatsapp-btn"
            className="bg-[#25D366] text-white font-display uppercase tracking-wider text-xs px-4 h-10 rounded-md inline-flex items-center gap-2 hover:brightness-110"
            title="Open WhatsApp Business with a pre-written message — free"
          >
            <MessageCircle size={14} /> WhatsApp
          </button>
          <button
            onClick={printDoc}
            data-testid="job-print-btn"
            className="border border-[#cfd7db] text-[#3f4b52] font-display uppercase tracking-wider text-xs px-4 h-10 rounded-md inline-flex items-center gap-2 hover:bg-[#f5f6f6]"
          >
            <Printer size={14} /> Print preview
          </button>
          {job.voided ? (
            <button
              onClick={doUnvoid}
              data-testid="job-unvoid-btn"
              className="border border-[#10b981] text-[#10b981] font-display uppercase tracking-wider text-xs px-4 h-10 rounded-md inline-flex items-center gap-2 hover:bg-[#10b981]/10"
            >
              <RotateCcw size={14} /> Reverse void
            </button>
          ) : (
            <button
              onClick={() => setShowDeleteModal(true)}
              data-testid="job-void-delete-btn"
              className="border border-[#dc2626] text-[#dc2626] font-display uppercase tracking-wider text-xs px-4 h-10 rounded-md inline-flex items-center gap-2 hover:bg-[#dc2626]/10"
            >
              <Trash2 size={14} /> Void / Delete
            </button>
          )}
        </div>
      </div>

      {job.voided && (
        <div className="mt-4 no-print bg-[#fef2f2] border-2 border-[#dc2626] rounded-md p-4 flex items-start gap-3">
          <AlertTriangle className="text-[#dc2626] shrink-0" size={20} />
          <div>
            <div className="font-display uppercase tracking-widest text-xs text-[#dc2626] font-bold">Invoice voided</div>
            {job.void_reason && <div className="text-sm text-[#7f1d1d] mt-0.5">Reason: {job.void_reason}</div>}
            {job.voided_at && <div className="text-xs text-[#7f1d1d]">Voided on {new Date(job.voided_at).toLocaleString("en-ZA")}</div>}
            <div className="text-xs text-[#7f1d1d] mt-1">Excluded from revenue, statements and outstanding reports. Documents display a &quot;VOID&quot; watermark.</div>
          </div>
        </div>
      )}

      {showDeleteModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="void-delete-modal">
          <div className="bg-white rounded-md shadow-xl max-w-lg w-full p-6">
            <div className="flex items-start justify-between">
              <div>
                <div className="font-display uppercase tracking-widest text-xs text-[#dc2626]">Cancel this invoice</div>
                <h2 className="font-display font-black uppercase text-xl text-[#111619] mt-1">Void or fully delete?</h2>
              </div>
              <button onClick={() => setShowDeleteModal(false)} className="text-[#3f4b52]"><X size={18} /></button>
            </div>
            <div className="mt-4 space-y-4">
              <div className="border border-[#cfd7db] rounded-md p-4">
                <div className="font-display uppercase tracking-widest text-xs text-[color:var(--brand-accent)] mb-1 inline-flex items-center gap-1"><Ban size={12} /> Void invoice (recommended)</div>
                <p className="text-xs text-[#3f4b52]">Keep the job & six documents in the system with a VOID watermark. Excluded from revenue, statements and outstanding reports. Can be reversed.</p>
                <div className="mt-3">
                  <label className="text-xs uppercase text-[#3f4b52] font-display tracking-widest">Reason (optional)</label>
                  <input
                    value={voidReason}
                    onChange={(e) => setVoidReason(e.target.value)}
                    placeholder="e.g. captured in error, wrong customer, duplicate…"
                    className="w-full mt-1 h-10 px-3 bg-white border border-[#cfd7db] rounded-md text-sm"
                    data-testid="void-reason-input"
                  />
                </div>
                <button
                  onClick={doVoid}
                  disabled={busy}
                  data-testid="confirm-void-btn"
                  className="mt-3 w-full bg-[color:var(--brand-accent)] text-white rounded-md py-2.5 font-display uppercase tracking-widest text-xs inline-flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  <Ban size={12} /> Void invoice
                </button>
              </div>

              <div className="border border-[#dc2626] rounded-md p-4">
                <div className="font-display uppercase tracking-widest text-xs text-[#dc2626] mb-1 inline-flex items-center gap-1"><Trash2 size={12} /> Delete job entirely</div>
                <p className="text-xs text-[#3f4b52]">All six documents disappear, job number is retired, any linked booking is also removed. This <b>cannot be undone</b>.</p>
                <button
                  onClick={doDelete}
                  disabled={busy}
                  data-testid="confirm-delete-btn"
                  className="mt-3 w-full bg-[#dc2626] text-white rounded-md py-2.5 font-display uppercase tracking-widest text-xs inline-flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  <Trash2 size={12} /> Delete permanently
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Vehicle history */}
      {job.registration && (
        <div className="mt-6 no-print">
          <VehicleHistory registration={job.registration} excludeJobId={job.id} />
        </div>
      )}

      {/* Pre-fitment sign-off link */}
      <div className="mt-6 no-print">
        <PreFitmentShareCard job={job} onShared={load} />
      </div>

      {/* Document tabs */}
      <div className="mt-8 no-print">
        <div className="flex flex-wrap gap-2 border-b border-[#cfd7db]">
          {visibleDocs.map((d) => (
            <button
              key={d.key}
              onClick={() => setActive(d.key)}
              data-testid={`doc-tab-${d.key}`}
              className={`px-4 py-2.5 font-display uppercase tracking-wider text-xs border-b-2 transition ${
                active === d.key
                  ? "border-[color:var(--brand-accent)] text-[color:var(--brand-accent)]"
                  : "border-transparent text-[#3f4b52] hover:text-[#111619]"
              }`}
            >
              {d.label}
            </button>
          ))}
        </div>
      </div>

      {/* Document content */}
      <div className="mt-6">
        {active === "tax_invoice" && !job.tax_invoice_number && (
          <div className="no-print mb-4 bg-white border-2 border-[color:var(--brand-accent)] rounded-md p-5 flex items-start justify-between gap-4 flex-wrap" data-testid="tax-invoice-issue-card">
            <div className="min-w-0">
              <div className="font-display uppercase tracking-widest text-xs text-[color:var(--brand-accent)]">Tax Invoice — not yet issued</div>
              <div className="font-display font-bold text-[#111619] mt-0.5">
                Ready to issue a SARS-compliant Tax Invoice for this job?
              </div>
              <p className="text-xs text-[#3f4b52] mt-1">
                A sequential invoice number (<b>INV####</b>) will be locked in and the tab below becomes the official tax invoice.
                Only allowed after the team submits the fitment, or once you mark the job <b>Completed</b>.
              </p>
              {!job.field_submission && job.status !== "completed" && (
                <div className="mt-2 text-[11px] text-[#dc2626]">
                  ⚠ Waiting for team submission or status = Completed.
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={doIssueTaxInvoice}
              disabled={busy || (!job.field_submission && job.status !== "completed")}
              data-testid="issue-tax-invoice-btn"
              className="bg-[color:var(--brand-accent)] text-white px-4 h-11 rounded-md font-display uppercase tracking-widest text-xs inline-flex items-center gap-2 disabled:opacity-50 whitespace-nowrap"
            >
              Issue Tax Invoice
            </button>
          </div>
        )}
        {active === "tax_invoice" && job.tax_invoice_number && (
          <div className="no-print mb-4 bg-[#ecfdf5] border border-[#10b981] text-[#065f46] rounded-md px-4 py-2 text-xs" data-testid="tax-invoice-issued-banner">
            ✓ Tax Invoice <b>{job.tax_invoice_number}</b> issued on{" "}
            {job.tax_invoice_issued_at ? new Date(job.tax_invoice_issued_at).toLocaleString("en-ZA") : "—"}.
          </div>
        )}
        {active === "field_submission" ? (
          <FieldSubmissionView submission={job.field_submission} />
        ) : (
          <JobDocuments job={job} activeDoc={active} />
        )}
      </div>

      {showClientLink && (
        <ClientLinkModal job={job} onClose={() => setShowClientLink(false)} />
      )}

      {showBundle && (
        <BundleSendModal job={job} onClose={() => setShowBundle(false)} />
      )}

      {showNotify && (
        <NotifyStatusModal job={job} onClose={() => setShowNotify(false)} />
      )}

      {showWhatsApp && (
        <WhatsAppQuickSendModal job={job} onClose={() => setShowWhatsApp(false)} />
      )}

      {showSignature && (
        <SignaturePadModal
          job={job}
          onClose={() => setShowSignature(false)}
          onSaved={load}
        />
      )}

      {showSchedule && (
        <JobScheduleModal
          job={job}
          onClose={() => setShowSchedule(false)}
          onSaved={load}
        />
      )}
    </div>
  );
};

export default AdminJobDetail;
